import { useEffect, useMemo, useState } from 'react';
import type {
  AlertConfigResponse,
  Channel,
  ChannelFlags,
  MatrixBySeverity,
  Severity,
} from '../../api/alertConfigApi';

// 권위: docs/ARCH_DASHBOARD.md §4.9 섹션 1 — Severity × Channel 4x4 매트릭스.
// UI 선제 차단: FATAL 행의 teams/email/webpush 중 최소 1개는 on 이어야 저장 가능.
// inapp 은 UI 전용 — FATAL 실패 조건에서 배제 (백엔드 스펙 동일).

const SEVERITIES: Severity[] = ['FATAL', 'ERROR', 'WARN', 'INFO'];
const CHANNELS: Channel[] = ['teams', 'email', 'webpush', 'inapp'];

export function isFatalRowValid(matrix: MatrixBySeverity): boolean {
  const f = matrix.FATAL;
  return f.teams || f.email || f.webpush;
}

function equalMatrix(a: MatrixBySeverity, b: MatrixBySeverity): boolean {
  for (const s of SEVERITIES) {
    for (const c of CHANNELS) {
      if (a[s][c] !== b[s][c]) return false;
    }
  }
  return true;
}

interface Props {
  config: AlertConfigResponse;
  onSave: (matrix: MatrixBySeverity) => void;
  isSaving: boolean;
}

export function MatrixSection({ config, onSave, isSaving }: Props): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<MatrixBySeverity>(config.matrix);

  // 서버에서 최신 config 가 오면 편집 중이 아닐 때만 싱크.
  useEffect(() => {
    if (!editing) setDraft(config.matrix);
  }, [config.matrix, editing]);

  const dirty = useMemo(
    () => !equalMatrix(draft, config.matrix),
    [draft, config.matrix]
  );
  const fatalValid = useMemo(() => isFatalRowValid(draft), [draft]);

  const toggle = (sev: Severity, ch: Channel): void => {
    setDraft((prev) => {
      const nextRow: ChannelFlags = { ...prev[sev], [ch]: !prev[sev][ch] };
      return { ...prev, [sev]: nextRow };
    });
  };

  const startEdit = (): void => {
    setDraft(config.matrix);
    setEditing(true);
  };
  const cancel = (): void => {
    setDraft(config.matrix);
    setEditing(false);
  };
  const save = (): void => {
    if (!fatalValid) return;
    onSave(draft);
    setEditing(false);
  };

  return (
    <section className="as-card" data-testid="as-matrix-section">
      <div className="as-section__head">
        <div>
          <h3 className="as-section__title">1. Severity × Channel 매트릭스</h3>
          <div className="as-section__sub">
            심각도별로 어떤 채널로 알림을 발송할지. FATAL 행은 teams / email / webpush 중 최소 1 개는 켜져 있어야 합니다.
          </div>
        </div>
        <div className="as-section__actions">
          {dirty && <span className="as-dirty">저장되지 않음</span>}
          {!editing ? (
            <button type="button" className="as-btn" onClick={startEdit}>
              편집
            </button>
          ) : (
            <>
              <button
                type="button"
                className="as-btn as-btn--ghost"
                onClick={cancel}
                disabled={isSaving}
              >
                취소
              </button>
              <button
                type="button"
                className="as-btn as-btn--primary"
                onClick={save}
                disabled={!fatalValid || !dirty || isSaving}
                data-testid="as-matrix-save"
              >
                저장
              </button>
            </>
          )}
        </div>
      </div>

      <table className="as-matrix" role="grid">
        <thead>
          <tr>
            <th className="as-matrix__sev">severity</th>
            {CHANNELS.map((c) => (
              <th key={c}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {SEVERITIES.map((sev) => {
            const row = draft[sev];
            const isFatalInvalid = sev === 'FATAL' && !fatalValid;
            return (
              <tr
                key={sev}
                className={
                  `as-matrix__row--${sev}` +
                  (isFatalInvalid ? ' as-matrix__row--invalid' : '')
                }
                data-testid={`as-matrix-row-${sev}`}
              >
                <td className="as-matrix__sev">{sev}</td>
                {CHANNELS.map((ch) => (
                  <td key={ch}>
                    <input
                      type="checkbox"
                      checked={row[ch]}
                      disabled={!editing || isSaving}
                      onChange={() => toggle(sev, ch)}
                      aria-label={`${sev} ${ch}`}
                      data-testid={`as-matrix-cell-${sev}-${ch}`}
                    />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>

      {!fatalValid && (
        <div className="as-warn" role="alert" data-testid="as-matrix-fatal-warn">
          FATAL 행 Teams / Email / WebPush 가 모두 꺼져 있습니다. 최소 1 개 채널이
          켜져 있어야 저장할 수 있습니다.
        </div>
      )}
    </section>
  );
}
